import { useEffect, useState, type JSX } from 'react';
import { useParams, useLocation, Link } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { PaintingSchema } from '../lib/schemas/painting';
import { formatDimensions } from '../lib/formatDimensions';
import type { Painting } from '../types/painting';

type ItemState = { status: 'loading' } | { status: 'error'; error: Error } | { status: 'ready'; painting: Painting };

export default function ItemPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const statePainting = (location.state as { painting?: Painting } | null)?.painting;

  const [state, setState] = useState<ItemState>(
    statePainting !== undefined ? { status: 'ready', painting: statePainting } : { status: 'loading' },
  );

  useEffect(() => {
    if (statePainting !== undefined) return;
    if (id === undefined) {
      setState({ status: 'error', error: new Error('No painting id in the URL') });
      return;
    }

    let cancelled = false;

    getDoc(doc(db, 'paintings', id))
      .then((snapshot): void => {
        if (cancelled) return;
        if (!snapshot.exists()) {
          setState({ status: 'error', error: new Error('Painting not found') });
          return;
        }
        const painting = PaintingSchema.parse({ id: snapshot.id, ...snapshot.data() });
        setState({ status: 'ready', painting });
      })
      .catch((fetchError: unknown): void => {
        if (cancelled) return;
        setState({
          status: 'error',
          error: fetchError instanceof Error ? fetchError : new Error('Failed to load painting'),
        });
      });

    return (): void => {
      cancelled = true;
    };
  }, [id, statePainting]);

  if (state.status === 'loading') {
    return (
      <main className="mx-auto max-w-3xl px-4 py-12 text-center text-neutral-500">
        <p>Loading painting…</p>
      </main>
    );
  }

  if (state.status === 'error') {
    return (
      <main className="mx-auto max-w-3xl px-4 py-12 text-center text-red-600">
        <p>{state.error.message}</p>
        <Link to="/" className="mt-4 inline-block text-blue-600 underline">
          Back to gallery
        </Link>
      </main>
    );
  }

  const { painting } = state;
  const isSold = painting.status === 'sold';

  return (
    <main className="mx-auto max-w-3xl px-4 py-12">
      <Link to="/" className="text-sm text-blue-600 underline">
        &larr; Back to gallery
      </Link>

      <div className="mt-4 flex flex-col gap-6 md:flex-row">
        <img src={painting.imageUrl} alt={painting.title} className="w-full rounded-md md:w-1/2" />

        <div>
          <h1 className="text-2xl font-bold text-neutral-900">{painting.title}</h1>
          <p className="mt-1 text-neutral-600">
            {painting.style.join(', ')} · {formatDimensions(painting.height, painting.width)}
          </p>

          {painting.category.length > 0 ? (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {painting.category.map((tag) => (
                <span key={tag} className="rounded-full bg-neutral-100 px-2.5 py-0.5 text-xs text-neutral-700">
                  {tag}
                </span>
              ))}
            </div>
          ) : null}

          <p className="mt-4">
            {isSold ? (
              <span className="inline-block rounded-full bg-neutral-900 px-4 py-1.5 text-sm text-white">Sold</span>
            ) : (
              <Link
                to="/contactMe"
                className="inline-block rounded bg-blue-600 px-5 py-2 text-white hover:bg-blue-700"
              >
                Enquire
              </Link>
            )}
          </p>
        </div>
      </div>
    </main>
  );
}
